"use client";
/* Live verifier (fairness.html inline script), same algorithm as
   lib/server/fair.ts:
     hex  = sha256(`${server}:${client}:${nonce}`)
     roll = (parseInt(hex.slice(0, 8), 16) % 1000000) / 10000
   then rollDrop over the chosen box's odds table. With ?txn=CVT-XXXXXX it
   fetches her own round record from /api/verify and checks it. */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { RARITY } from "@/lib/catalog/data";
import { money, rollDrop } from "@/lib/catalog/logic";
import type { ClientBox, RarityKey } from "@/lib/catalog/types";
import { useCovet } from "@/components/providers/CovetProvider";

async function sha256hex(str: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

interface Round {
  txn: string; caseId: string; boxName: string; ts: string;
  serverSeed: string; serverHash: string; clientSeed: string; nonce: number; roll: number;
  dropId: string; drop: { house: string; name: string; size: string; rarity: RarityKey; odds: number };
}

const INTRO = "Edit any field and recompute. The same inputs always produce the same piece.";

export function Verifier({ txn }: { txn: string | null }) {
  const { catalog, box } = useCovet();
  const [server, setServer] = useState("a7c1e9f3b2d8406c5e1f9a3b7d2c8e40");
  const [hash, setHash] = useState("");
  const [client, setClient] = useState("riley-lucky-13");
  const [nonce, setNonce] = useState("7");
  const [caseId, setCaseId] = useState(catalog.cases[0]?.id || "");
  const [round, setRound] = useState<Round | null>(null);
  const [result, setResult] = useState<ReactNode>(txn ? `Loading pull #${txn}…` : INTRO);

  /* the box list is CASES; a verified pull from a free or wishlist box adds its own */
  const boxes = useMemo(() => {
    const list: ClientBox[] = [...catalog.cases];
    if (round && !list.some((c) => c.id === round.caseId)) {
      const extra = box(round.caseId);
      if (extra) list.push(extra);
    }
    return list;
  }, [catalog.cases, round, box]);

  useEffect(() => { let live = true; sha256hex(server).then((h) => { if (live) setHash(h); }); return () => { live = false; }; }, [server]);

  const verify = useCallback(async (v: { server: string; client: string; nonce: string; caseId: string }, check?: Round | null) => {
    const kase = boxes.find((c) => c.id === v.caseId) || boxes[0];
    if (!kase || !kase.drops.length) return;
    const hex = await sha256hex(`${v.server}:${v.client}:${v.nonce}`);
    const roll = (parseInt(hex.slice(0, 8), 16) % 1000000) / 10000; /* 0 to 99.9999 */
    const drop = rollDrop(kase, roll);
    const rar = RARITY[drop.rarity];
    let verdict: ReactNode = null;
    if (check) {
      const rollOk = roll.toFixed(4) === check.roll.toFixed(4);
      const dropOk = drop.id === check.dropId;
      verdict = rollOk && dropOk
        ? <><br /><span className="roll">✓ Matches your pull #{check.txn}</span> · roll {check.roll.toFixed(4)}, {check.drop.house} {check.drop.name}</>
        : rollOk
          ? <><br /><span className="roll">✓ Roll matches pull #{check.txn}</span> · it landed {check.drop.house} {check.drop.name} on the odds table in place at the time.</>
          : <><br /><b>✗ Doesn&apos;t match pull #{check.txn}</b> · recorded roll {check.roll.toFixed(4)}, {check.drop.house} {check.drop.name}</>;
    }
    setResult(<>
      Digest <code style={{ fontSize: 11 }}>{hex.slice(0, 16)}…</code> → roll <span className="roll">{roll.toFixed(4)}</span><br />
      <b>{drop.house} {drop.name}</b> · {drop.size} · <span className="rarity-chip" style={{ ["--rar" as string]: rar.color }}>{rar.label}</span>
      {verdict}
    </>);
  }, [boxes]);

  /* ?txn=: prefill from her own round record, then auto-verify */
  useEffect(() => {
    if (!txn) return;
    let live = true;
    fetch(`/api/verify/${encodeURIComponent(txn)}`, { cache: "no-store", credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<Round>) : null))
      .then((r) => {
        if (!live) return;
        if (!r) { setResult(`We couldn't find pull #${txn} on your shelf. ${INTRO}`); return; }
        setServer(r.serverSeed); setClient(r.clientSeed); setNonce(String(r.nonce)); setCaseId(r.caseId);
        setRound(r);
      })
      .catch(() => { if (live) setResult(INTRO); });
    return () => { live = false; };
  }, [txn]);

  /* once the round (and its box option) is in place, run the check */
  useEffect(() => {
    if (round) void verify({ server: round.serverSeed, client: round.clientSeed, nonce: String(round.nonce), caseId: round.caseId }, round);
  }, [round, verify]);

  const isRound = !!round && server === round.serverSeed && client === round.clientSeed && nonce === String(round.nonce) && caseId === round.caseId;

  return (
    <div className="verifier">
      <h3>Live verifier</h3>
      <div className="field">
        <label htmlFor="vServer">Server seed (revealed)</label>
        <input id="vServer" value={server} onChange={(e) => setServer(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="vHash">Server seed hash (published before your pulls)</label>
        <input id="vHash" readOnly value={hash} />
      </div>
      <div className="field">
        <label htmlFor="vClient">Your client seed</label>
        <input id="vClient" value={client} onChange={(e) => setClient(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="vNonce">Nonce (pull number)</label>
        <input id="vNonce" type="number" min={0} value={nonce} onChange={(e) => setNonce(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="vCase">Box</label>
        <select id="vCase" value={caseId} onChange={(e) => setCaseId(e.target.value)}>
          {boxes.map((c) => <option key={c.id} value={c.id}>{c.name} · {money(c.price)}</option>)}
        </select>
      </div>
      <button className="btn btn-gold" style={{ width: "100%" }} onClick={() => verify({ server, client, nonce, caseId }, isRound ? round : null)}>Recompute this pull</button>
      <div className="verify-result" id="vResult">{result}</div>
    </div>
  );
}
